import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { handleDetails, TimeSheetPageState } from '../../slices/timesheet/timesheetPageSlice'
import {
	acceptApproval,
	ApprovalRequest,
} from '../../slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { handleInitialApprovalServices } from '../../../utils/middleware/initialServices'
import { handleInitialWorkTimes } from '../../../utils/middleware/workTimes'
import { changeBonus } from '../../slices/timesheet/timesheetBonusCodes'
import {
	handleInitialBenefitsForApprovalSubmit,
	handleInitialBonusCodesPageDetail,
} from '../../../utils/middleware/benefitCodes'
import getBaseQueryWithReauth from '../../../utils/api/query-auth'
import { handleError } from '../../../utils/errorHandler'
import getAuth from '../../../utils/api/getToken'
import { TimesheetStoreState } from '../../timesheetStore'

const baseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}/Timesheet`,
	prepareHeaders: headers => {
		const token = getAuth()
		if (token) headers.set('authorization', `Bearer ${token.auth}`)
		return headers
	},
})

const buildServicesWithExpandedState = (
	timesheetPage: TimesheetStoreState['timesheetPage'],
	services: TimeSheetPageState['services'],
	timeSheetId: number,
) => {
	if (timesheetPage.timeSheetId !== timeSheetId) {
		return services.map(service => ({
			...service,
			isExpanded: false,
		}))
	}

	return services.map(service => ({
		...service,
		isExpanded:
			timesheetPage.services.find(s => s.service.serviceId === service.service.serviceId)?.isExpanded ?? false,
	}))
}

export const timesheetDetailApi = createApi({
	reducerPath: 'timesheetDetail',
	baseQuery: getBaseQueryWithReauth(baseQuery),
	keepUnusedDataFor: 0,
	tagTypes: ['TimesheetDetail'],
	endpoints: builder => ({
		getTimesheet: builder.query<TimeSheetPageState, number>({
			query: (timesheetId: number) => {
				return `/getTimesheetById/${timesheetId}`
			},
			providesTags: (_result, _error, timesheetId) => [
				{ type: 'TimesheetDetail', id: timesheetId },
				{ type: 'TimesheetDetail' },
			],
			async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
				try {
					const { data } = await queryFulfilled
					const {
						approval,
						bonusCodes,
						comments,
						documents,
						driver,
						initialStatus,
						isBlocked,
						isSubmitted,
						services,
						timeSheetDate,
						timeSheetId,
						vehicles,
						workTimes,
						workTypeId,
						status,
						dispRecalculateOnSave: DispRecalculateOnSave,
						dispDrivingBreak: DispDrivingBreak,
						uniqueAgent: UniqueAgent,
						editedBy: EditedBy,
						editedDate: EditedDate,
						refunds: Refunds,
						refund: Refund,
						extraTimePayment: ExtraTimePayment,
					} = data

					const state = getState() as TimesheetStoreState
					const timesheetPage = state.timesheetPage
					const servicesWithExpandedState = buildServicesWithExpandedState(timesheetPage, services, timeSheetId)

					// Capture the refunds the reviewer had set just before this refetch (e.g. right after
					// clicking "Aprovar"). The server may return approval.refunds = null for a newly created
					// approval, so we use this as a fallback to prevent values from resetting to zero.
					const previousRefunds = state.timesheetApprovalDetailsAccept.refunds

					// Ensure approval.refunds survives in timesheetPage even when the server returns null
					// (happens on first approval before the record is fully committed).
					const approvalForDispatch = approval != null
						? { ...approval, refunds: approval.refunds ?? previousRefunds }
						: approval

					dispatch(
						handleDetails({
							extraTimePayment: ExtraTimePayment ?? false,
							documents,
							bonusCodes,
							initialStatus,
							comments,
							driver,
							isBlocked,
							isSubmitted,
							services: servicesWithExpandedState,
							timeSheetDate,
							timeSheetId,
							vehicles,
							workTimes,
							approval: approvalForDispatch,
							workTypeId,
							status,
							dispRecalculateOnSave: DispRecalculateOnSave,
							dispDrivingBreak: DispDrivingBreak,
							uniqueAgent: UniqueAgent,
							editedBy: EditedBy,
							editedDate: EditedDate,
							refunds: Refunds,
							refund: Refund,
							rowVersion: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
						}),
					)
					if (approval === null) {
						dispatch(
							changeBonus({
								bonusCodes: handleInitialBonusCodesPageDetail(bonusCodes),
							}),
						)
						dispatch(
							acceptApproval({
								timesheetId: data.timeSheetId,
								comments: data.comments,
								refund: Refund ?? 0,
								refunds: Refunds,
								workTypeId: data.workTypeId,
								hasChangesFromReviewer: false,
								extraTimePayment: ExtraTimePayment ?? false,
								dispRecalculateOnSave: DispRecalculateOnSave ?? true,
								dispDrivingBreak: DispDrivingBreak ?? null,
								uniqueAgent: UniqueAgent ?? null,
								initialStatus: data.initialStatus ?? {
									kmsId: 0,
									kmsIni: 0,
									regDate: '',
									inEmpty: false,
									isClean: false,
								},
								services: handleInitialApprovalServices(data),
								workTimes: handleInitialWorkTimes(data, false),
								benefits: handleInitialBenefitsForApprovalSubmit(data.bonusCodes),
							} as ApprovalRequest),
						)
					}
					if (data?.approval !== null) {
						dispatch(
							changeBonus({
								bonusCodes: handleInitialBonusCodesPageDetail(data.approval!.timeSheetApprovalCodes),
							}),
						)
						const hasApprovalId = (data?.approval?.timeSheetApprovalId ?? 0) > 0
						dispatch(
							acceptApproval({
								refunds: hasApprovalId
									? (data.approval!.refunds ?? previousRefunds ?? Refunds)
									: (previousRefunds ?? Refunds),
								workTimes: handleInitialWorkTimes(data, true),
								timesheetId: data.timeSheetId,
								comments: data.approval!.comments,
								refund: hasApprovalId ? (data.approval!.refund ?? Refund ?? 0) : Refund ?? 0,
								workTypeId: data.approval!.workTypeId,
								hasChangesFromReviewer: false,
								benefits: [],
								services: handleInitialApprovalServices(data),
								initialStatus: data.initialStatus ?? {
									kmsId: 0,
									kmsIni: 0,
									regDate: '',
									inEmpty: false,
									isClean: false,
								},
								extraTimePayment: data.approval!.extraTimePayment,
								dispRecalculateOnSave: DispRecalculateOnSave ?? true,
								dispDrivingBreak: DispDrivingBreak ?? null,
								uniqueAgent: UniqueAgent ?? null,
							} as ApprovalRequest),
						)
					}
				} catch (error) {
					handleError(error, 'timesheetDetailApi.ts')
				}
			},
		}),
	}),
})

// Export hooks for usage in functional components
export const { useGetTimesheetQuery } = timesheetDetailApi
